import { StopId, TrainIdentifier } from "@vygruppen/vy-train-map";
import {
  RouteSection,
  TrainCustomEventSeverityEnum,
  TrainMissingProductsCoverageEnum,
  TrainMissingProductsEnum,
} from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/types/formSchema";
import { CapacityUnit } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/types/trainCommonFieldtypes";
import { TrainEventTypeEnum } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/types/trainEventTypeEnum";
import { distributionTexts } from "features/CenterContent/shared/operationalInformation/utils";
import { PrognosisResponse } from "features/CenterContent/shared/operationalInformation/prognosisUtils";

type DropsLogText = {
  NOB: string;
};

type TrainStoppedDetailedResponse = {
  type: TrainEventTypeEnum.TRAIN_STOPPED;
  trainRouteSection?: RouteSection;
  dropsLogText?: DropsLogText;
};

type TrainCancelledDetailedResponse = {
  type: TrainEventTypeEnum.TRAIN_CANCELLED;
  trainRouteSection: RouteSection;
  dropsLogText?: DropsLogText;
};

type TrainCapacityDetailedResponse = {
  type:
    | TrainEventTypeEnum.TRAIN_CAPACITY_REDUCED
    | TrainEventTypeEnum.TRAIN_CAPACITY_INCREASED;
  trainRouteSection: RouteSection;
  oldCapacity: number[];
  newCapacity: number[];
  oldCapacityUnit: CapacityUnit;
  newCapacityUnit: CapacityUnit;
  dropsLogText?: DropsLogText;
};

type TrainDelayedDetailedResponse = {
  type: TrainEventTypeEnum.TRAIN_DELAYED;
  trainRouteSection: RouteSection;
  delayInMinutes?: number;
  dropsLogText?: DropsLogText;
};

type TrainLateToTrackDetailedResponse = {
  type: TrainEventTypeEnum.TRAIN_LATE_TO_TRACK;
  trainRouteSection: RouteSection;
  minutesLate?: number;
  dropsLogText?: DropsLogText;
};

type TrainDelayExpectedDetailedResponse = {
  type: TrainEventTypeEnum.TRAIN_DELAY_EXPECTED;
  trainRouteSection: RouteSection;
  delayInMinutes?: number;
  dropsLogText?: DropsLogText;
};

type TrainClosedSetDetailedResponse = {
  type: TrainEventTypeEnum.TRAIN_CLOSED_SET;
  trainRouteSection: RouteSection;
  dropsLogText?: DropsLogText;
};

type TrainNotStoppingAtStationDetailedResponse = {
  type: TrainEventTypeEnum.TRAIN_NOT_STOPPING_AT_STATION;
  skippedStops: StopId[];
  dropsLogText?: DropsLogText;
};

type TrainMissingProductDetailedResponse = {
  type: TrainEventTypeEnum.TRAIN_MISSING_PRODUCT;
  trainRouteSection: RouteSection;
  products: TrainMissingProductsEnum;
  coverage: TrainMissingProductsCoverageEnum;
  dropsLogText?: DropsLogText;
};

type TrainStoppingExtraAtStationDetailedResponse = {
  type: TrainEventTypeEnum.TRAIN_STOPPING_EXTRA_AT_STATION;
  extraStops: StopId[];
  dropsLogText?: DropsLogText;
};

type CustomDistributions = {
  contextTypes: string[];
  texts: DropsLogText;
};

type TrainCustomDetailedResponse = {
  type:
    | TrainEventTypeEnum.TRAIN_GENERAL_INFO
    | TrainEventTypeEnum.TRAIN_GENERAL_WARNING;
  trainRouteSection: RouteSection;
  severity: TrainCustomEventSeverityEnum;
  distributions: CustomDistributions;
  dropsLogText?: undefined;
};

type TrainChangedRouteDetailedResponse = {
  type: TrainEventTypeEnum.TRAIN_CHANGED_ROUTE;
  affectedStops: StopId[];
  dropsLogText?: DropsLogText;
};

type CommonFields = {
  uuid: string;
  trainIdentifier: TrainIdentifier;
  reason: { type: string; description?: typeof distributionTexts };
  action: { type: string; description?: typeof distributionTexts };
  prognosis: PrognosisResponse;
  incidentIds?: string[];
};

export type TrainInformationDetailedResponse = (
  | TrainStoppedDetailedResponse
  | TrainCancelledDetailedResponse
  | TrainCapacityDetailedResponse
  | TrainDelayedDetailedResponse
  | TrainLateToTrackDetailedResponse
  | TrainDelayExpectedDetailedResponse
  | TrainClosedSetDetailedResponse
  | TrainNotStoppingAtStationDetailedResponse
  | TrainMissingProductDetailedResponse
  | TrainStoppingExtraAtStationDetailedResponse
  | TrainCustomDetailedResponse
  | TrainChangedRouteDetailedResponse
) &
  CommonFields;

export function trainResponseIsTrainCustomResponse(
  trainResponse: TrainInformationDetailedResponse,
): trainResponse is TrainCustomDetailedResponse & CommonFields {
  // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check -- single special case
  switch (trainResponse.type) {
    case TrainEventTypeEnum.TRAIN_GENERAL_INFO:
    case TrainEventTypeEnum.TRAIN_GENERAL_WARNING:
      return true;

    default:
      return false;
  }
}
